/**
 * Utility functions for managing theme colors in the rich text editor
 */

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  muted: string;
  foreground: string;
  background: string;
}

/**
 * Note color mappings: Light Mode → Dark Mode
 */
const NOTE_COLOR_MAP: Record<string, string> = {
  '#F2FCE2': '#0B0D09', // Light Green → Dark Green
  '#FEF7CD': '#17150E', // Light Yellow → Dark Yellow
  '#FEC6A1': '#1F1815', // Light Orange → Dark Orange
  '#E5DEFF': '#110E14', // Light Purple → Dark Purple
  '#FFDEE2': '#1A0E10', // Light Pink → Dark Pink
  '#D3E4FD': '#0A131A', // Light Blue → Dark Blue
};

/**
 * Get theme-aware color based on current theme
 */
export function getThemeAwareColor(color: string, isDark: boolean): string {
  if (!isDark) return color;
  
  // Return dark variant if available, otherwise return original
  return NOTE_COLOR_MAP[color.toUpperCase()] || color;
}

/**
 * Get theme-appropriate colors based on dark/light mode
 */
export function getThemeColors(isDark: boolean): ThemeColors {
  return {
    primary: isDark ? '#3b82f6' : '#2563eb',
    secondary: isDark ? '#8b5cf6' : '#7c3aed',
    accent: isDark ? '#10b981' : '#059669',
    muted: isDark ? '#6b7280' : '#9ca3af',
    foreground: isDark ? '#f3f4f6' : '#1f2937',
    background: isDark ? '#1f2937' : '#ffffff',
  };
}

/**
 * Get HSL color value from CSS variable
 */
export function getThemeColor(colorName: string): string;
export function getThemeColor(color: string, isDark: boolean): string;
export function getThemeColor(colorOrName: string, isDark?: boolean): string {
  // If isDark is provided, use the theme-aware color function
  if (isDark !== undefined) {
    return getThemeAwareColor(colorOrName, isDark);
  }
  
  // Otherwise, get color from CSS variable
  if (typeof window === 'undefined') return '';
  
  const root = document.documentElement;
  const value = getComputedStyle(root).getPropertyValue(`--${colorOrName}`).trim();
  
  if (value) {
    return `hsl(${value})`;
  }
  
  return '';
}

/**
 * Convert various color formats to a canonical format
 */
export function getCanonicalColor(color: string): string {
  // If it's already an HSL value, return as is
  if (color.startsWith('hsl(')) {
    return color;
  }
  
  // If it's a hex color, convert to hsl
  if (color.startsWith('#')) {
    return hexToHSL(color);
  }
  
  // If it's an RGB value, convert to hsl
  if (color.startsWith('rgb')) {
    return rgbToHSL(color);
  }
  
  // Otherwise return as is
  return color;
}

/**
 * Convert hex color to HSL
 */
function hexToHSL(hex: string): string {
  // Remove the hash if present
  hex = hex.replace(/^#/, '');
  
  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
}

/**
 * Convert RGB color to HSL
 */
function rgbToHSL(rgb: string): string {
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return rgb;
  
  const r = parseInt(match[1]) / 255;
  const g = parseInt(match[2]) / 255;
  const b = parseInt(match[3]) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
}
